(function () {
  'use strict';

  /**
   * Port Traffic Reports — hub & country listing filter (static front-end).
   *
   * Attribute contract (see port-traffic-reports.njk / -country.njk):
   *   [data-ptr]                 root wrapper (filters + results + empty state)
   *   [data-ptr-scroll]          element scrolled into view on filter change
   *   [data-ptr-filter="<key>"]  <select> filters (key = URL param, e.g. region/month/edition)
   *   [data-ptr-search]          text search input (URL param "q")
   *   [data-ptr-search-clear]    clear (×) button inside the search field
   *   [data-ptr-reset]           reset button(s)
   *   [data-ptr-results]         container that holds the groups/cards
   *   [data-ptr-group]           group wrapper (hidden when it has no visible cards)
   *   [data-ptr-item]            card wrapper — carries data-<key> + data-text for matching
   *   [data-ptr-empty]           "no results" block (d-none until needed)
   *
   * The backend build duplicates this module and swaps the client-side filter in
   * render() for a real AJAX call using the same URL params.
   */

  const PARAM_KEYS = ['region', 'month', 'edition', 'q'];
  const initPortTrafficReports = () => {
    const root = document.querySelector('[data-ptr]');
    if (!root) return;
    const results = root.querySelector('[data-ptr-results]');
    if (!results) return;
    const empty = root.querySelector('[data-ptr-empty]');
    const filters = [...root.querySelectorAll('[data-ptr-filter]')];
    const searchInput = root.querySelector('[data-ptr-search]');
    const searchClear = root.querySelector('[data-ptr-search-clear]');
    const resetButtons = [...root.querySelectorAll('[data-ptr-reset]')];
    const scrollTarget = root.querySelector('[data-ptr-scroll]') || root;
    const originalHtml = results.innerHTML;
    const $ = window.jQuery;
    let silent = false;
    let searchTimer = null;

    // ── URL <-> controls ──────────────────────────────────────────────────────
    const currentParams = () => {
      const params = {};
      filters.forEach(el => {
        const key = el.getAttribute('data-ptr-filter');
        const val = (el.value || '').trim().toLowerCase();
        if (val && val !== 'all') params[key] = val;
      });
      if (searchInput && searchInput.value.trim()) params.q = searchInput.value.trim();
      return params;
    };

    // Browser extensions routinely wrap history.replaceState and some of those
    // wrappers throw (Firefox logs "Not allowed to define cross-origin object as
    // property … XrayWrapper"). The URL is bookkeeping only, so it must never be
    // able to abort filtering. (Production carried this guard as a hand edit of
    // the built file since #2005; this is the same guard in the source.)
    const writeUrl = params => {
      try {
        const url = new URL(window.location.href);
        PARAM_KEYS.forEach(k => url.searchParams.delete(k));
        Object.keys(params).forEach(k => url.searchParams.set(k, params[k]));
        window.history.replaceState(null, '', url.pathname + url.search);
      } catch (e) {
        // cosmetic failure, the grid is already correct
      }
    };
    const readUrl = () => {
      const url = new URL(window.location.href);
      const params = {};
      PARAM_KEYS.forEach(k => {
        const v = url.searchParams.get(k);
        if (v) params[k] = v;
      });
      return params;
    };
    const toggleClear = () => {
      if (!searchClear) return;
      const has = !!(searchInput && searchInput.value.trim().length);
      searchClear.classList.toggle('d-none', !has);
    };
    const setControls = params => {
      silent = true;
      filters.forEach(el => {
        const key = el.getAttribute('data-ptr-filter');
        el.value = params[key] || 'all';
        if ($ && $(el).hasClass('select2-hidden-accessible')) $(el).trigger('change.select2');
      });
      if (searchInput) searchInput.value = params.q || '';
      toggleClear();
      silent = false;
    };

    // ── Rendering ─────────────────────────────────────────────────────────────
    const matches = (item, params) => Object.keys(params).every(key => {
      if (key === 'q') {
        return (item.getAttribute('data-text') || '').toLowerCase().includes(params.q.toLowerCase());
      }
      return (item.getAttribute(`data-${key}`) || '').toLowerCase() === String(params[key]).toLowerCase();
    });

    // The card wrapper carries `d-flex`, and app.css emits `.d-none` before
    // `.d-flex` (Bootstrap 4 order, both !important), so toggling `d-none` on a
    // card never hid it: the month filter only looked right because it hides
    // whole groups, which have no `d-flex`. An inline `display: none !important`
    // outranks every stylesheet rule; `d-none` is still toggled for anything
    // that keys off it.
    const setVisible = (el, visible) => {
      el.classList.toggle('d-none', !visible);
      if (visible) el.style.removeProperty('display');else el.style.setProperty('display', 'none', 'important');
    };
    const render = params => {
      results.innerHTML = originalHtml;
      const items = [...results.querySelectorAll('[data-ptr-item]')];
      let visible = 0;
      items.forEach(item => {
        const ok = matches(item, params);
        setVisible(item, ok);
        if (ok) visible += 1;
      });

      // Hide group headers/wrappers that have no visible cards (hub).
      results.querySelectorAll('[data-ptr-group]').forEach(group => {
        const any = [...group.querySelectorAll('[data-ptr-item]')].some(i => !i.classList.contains('d-none'));
        setVisible(group, any);
      });
      const none = visible === 0;
      setVisible(results, !none);
      if (empty) setVisible(empty, none);
    };

    // Filtering is client-side, so there is nothing to wait for. The prototype
    // showed skeleton cards for a fake 550 ms "fetch" before rendering; that
    // window let a second change land in between and left the grid showing an
    // earlier filter than the selects and the URL. Render first, from the
    // controls as they are right now, then do the cosmetic work.
    const apply = ({
      scroll = true
    } = {}) => {
      const params = currentParams();
      render(params);
      writeUrl(params);
      if (scroll && scrollTarget) {
        try {
          scrollTarget.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        } catch (e) {
          // cosmetic
        }
      }
    };
    const reset = () => {
      setControls({});
      writeUrl({});
      render({});
    };

    // ── Events ──────────────────────────────────────────────────────────────
    // select2 fires its change via jQuery.trigger('change'), which native
    // addEventListener does NOT catch — bind through jQuery when available.
    filters.forEach(el => {
      const handler = () => {
        if (silent) return;
        apply();
      };
      if ($) $(el).on('change', handler);else el.addEventListener('change', handler);
    });
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        toggleClear();
        if (silent) return;
        clearTimeout(searchTimer);
        searchTimer = setTimeout(() => apply(), 400);
      });
      searchInput.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          clearTimeout(searchTimer);
          apply();
        }
      });
    }
    if (searchClear) {
      searchClear.addEventListener('click', () => {
        if (searchInput) searchInput.value = '';
        toggleClear();
        apply();
      });
    }
    resetButtons.forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        reset();
      });
    });

    // Back/forward navigation → reflect URL params.
    window.addEventListener('popstate', () => {
      const params = readUrl();
      setControls(params);
      render(params);
    });

    // Initial load: reflect any params already in the URL (refresh-safe).
    const initial = readUrl();
    setControls(initial);
    if (Object.keys(initial).length) render(initial);else toggleClear();
  };
  (() => {
    document.addEventListener('DOMContentLoaded', () => {
      initPortTrafficReports();
    });
  })();

})();
